import { Footer } from "@/components/layout/Footer";
import { Navigation } from "@/components/layout/Navigation";
import { Container } from "@/components/ui/Container";
import { useScrollToHash } from "@/lib/use-scroll-to-hash";
import { Link } from "react-router-dom";

const EFFECTIVE_DATE = "May 15, 2026";
const CONTACT_EMAIL = "info@mobeencenter.org";

export function PrivacyPage() {
  useScrollToHash();
  return (
    <>
      <Navigation
        entries={[
          { label: "Events", to: "/#events" },
          { label: "Donate", to: "/donate" },
        ]}
        homePath="/"
      />
      <main className="flex-1">
        <Container size="narrow" className="py-16 lg:py-24">
          <p className="eyebrow text-accent">Legal</p>
          <h1 className="display-title mt-3 text-[clamp(2rem,5vw,3.4rem)] leading-tight">
            Privacy Policy
          </h1>
          <p className="mt-4 text-sm text-muted">Effective {EFFECTIVE_DATE}</p>

          <Section title="Who we are">
            <p>
              Mobeen Center (the &ldquo;Center,&rdquo; &ldquo;we,&rdquo; &ldquo;us&rdquo;) operates
              the website at <code>mobeen.pages.dev</code> and any associated subdomains. This
              policy explains what information we collect when you visit the site, register for an
              event, sign up for a potluck or competition, or make a donation, and what we do with
              that information.
            </p>
          </Section>

          <Section title="Information we collect">
            <h3 className="mt-4 font-display text-lg text-ink">When you register for an event</h3>
            <p>
              Event registration (RSVP) collects a family or household name, the number of adults
              and children attending, and a passcode you choose so you can edit your reservation
              later. Some events also ask for a contact email or phone number. You decide what to
              enter; please do not include sensitive details we do not ask for.
            </p>

            <h3 className="mt-6 font-display text-lg text-ink">When you sign up for a potluck</h3>
            <p>
              Potluck signups collect a name, the dish you plan to bring, a category (for example
              Main, Appetizer, Dessert), and the same edit passcode pattern described above.
            </p>

            <h3 className="mt-6 font-display text-lg text-ink">When you enter a competition</h3>
            <p>
              Competition entries (for example a cake competition) collect the baker&rsquo;s name,
              the entry title or description, and contact information used to reach you on event
              day.
            </p>

            <h3 className="mt-6 font-display text-lg text-ink">When you donate</h3>
            <p>
              Donation pages let you contribute to the Center&rsquo;s general fund or to a specific
              event campaign. Payment information (card number, expiration, security code) is
              collected and processed by <strong>Stripe, Inc.</strong>; we never see or store full
              card details. Stripe shares back a transaction reference, the amount, the donor name,
              and the donor email so we can acknowledge the gift and route it to the correct
              campaign.
            </p>

            <h3 className="mt-6 font-display text-lg text-ink">When you visit the site</h3>
            <p>
              Our hosting provider, <strong>Cloudflare</strong>, records standard server logs
              including the requesting IP address, browser type, requested URL, and timestamp. These
              logs are used for security, abuse prevention, and operational debugging. We do not run
              third-party advertising or analytics that build behavioral profiles.
            </p>
          </Section>

          <Section title="Cookies and local storage">
            <p>
              The public site uses a small amount of browser storage: a theme preference
              (light/dark) saved in <code>localStorage</code>, and an HTTP-only session cookie set
              only when an admin signs in to the management panel. We do not set cookies for
              advertising or cross-site tracking.
            </p>
          </Section>

          <Section title="How we use the information">
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>To operate registrations, potluck lists, and competition entries.</li>
              <li>To process and acknowledge donations.</li>
              <li>
                To send transactional emails (receipts, registration confirmations) when applicable.
              </li>
              <li>To investigate abuse, fraud, or security incidents.</li>
              <li>To meet recordkeeping requirements for charitable contributions.</li>
            </ul>
            <p className="mt-3">
              We do not sell personal information. We do not share personal information with third
              parties for their own marketing.
            </p>
          </Section>

          <Section title="Who we share information with">
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>
                <strong>Stripe</strong>: payment processing. Stripe is its own data controller for
                payment data; see Stripe&rsquo;s privacy notice.
              </li>
              <li>
                <strong>Cloudflare</strong>: site hosting, content delivery, and storage of event
                documents and submissions. Cloudflare processes data on our behalf.
              </li>
              <li>
                <strong>Lawful requests</strong>: we may disclose information if required by law,
                subpoena, or to protect the rights and safety of the Center, our members, or others.
              </li>
            </ul>
          </Section>

          <Section title="Children">
            <p>
              Some events include programs intended for children (for example a junior program).
              When children participate, we collect only the minimum information needed to run the
              activity, and we collect it from a parent or guardian, not from the child directly. We
              do not knowingly collect personal information from anyone under 13 without verifiable
              parental consent. If you believe a child has provided information to us, email{" "}
              <Mail /> and we will delete it.
            </p>
          </Section>

          <Section title="How long we keep information">
            <p>
              Event submissions (RSVP, potluck, competition entries) are retained for the duration
              of the event and for a reasonable archival period afterward, typically up to two
              years. Donation records are retained for as long as required by tax and accounting
              rules. You can request earlier deletion of your own submissions by contacting us at{" "}
              <Mail />.
            </p>
          </Section>

          <Section title="Security">
            <p>
              We protect data in transit with HTTPS, store admin passwords as salted hashes, and
              restrict administrative access to authorized staff. No system is perfectly secure;
              please use a passcode you do not reuse on other sites for event edits.
            </p>
          </Section>

          <Section title="Your choices">
            <p>
              You can ask us to access, correct, or delete the personal information you have
              provided by emailing <Mail />. We will respond within a reasonable time, typically
              within 30 days.
            </p>
          </Section>

          <Section title="Notice for California residents">
            <p>
              The California Consumer Privacy Act, as amended by the California Privacy Rights Act
              (together, the &ldquo;CCPA&rdquo;), gives California residents specific rights. In the
              past 12 months we have collected the categories of personal information described
              above (identifiers, commercial information for donations, internet and network
              activity, and information you voluntarily submit such as your name and family count).
              We did not <strong>sell</strong> personal information and did not{" "}
              <strong>share</strong> it for cross-context behavioral advertising.
            </p>
            <p className="mt-3">As a California resident you have the right to:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>Know what personal information we have collected and how we use it.</li>
              <li>Request a copy of that information.</li>
              <li>Request correction of inaccurate information.</li>
              <li>
                Request deletion of your information, subject to legal exceptions (for example,
                donation receipts retained for tax purposes).
              </li>
              <li>
                Opt out of any future sale or sharing of personal information. We do not currently
                sell or share, and if that changes we will update this notice and provide a
                &ldquo;Do Not Sell or Share My Personal Information&rdquo; link.
              </li>
              <li>Not receive retaliation for exercising any of these rights.</li>
            </ul>
            <p className="mt-3">
              You may exercise these rights by emailing <Mail />. You may use an authorized agent by
              providing written authorization that we can verify. We do not knowingly collect
              sensitive personal information beyond what you voluntarily provide; we do not use
              sensitive personal information to infer characteristics about you.
            </p>
          </Section>

          <Section title="Changes to this policy">
            <p>
              We may update this policy. Material changes will be announced on this page with a new
              effective date.
            </p>
          </Section>

          <Section title="Contact us">
            <p>
              Privacy questions, data requests, and corrections: <Mail />. For donation-specific
              concerns please also review our{" "}
              <Link to="/terms#refunds" className="text-accent underline-offset-4 hover:underline">
                Refund Policy
              </Link>
              .
            </p>
          </Section>
        </Container>
      </main>
      <Footer />
    </>
  );
}

function Section({
  title,
  id,
  children,
}: {
  title: string;
  id?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-10 scroll-mt-24" id={id}>
      <h2 className="font-display text-2xl font-light text-ink">{title}</h2>
      <div className="mt-3 space-y-3 text-pretty leading-relaxed text-muted">{children}</div>
    </section>
  );
}

function Mail() {
  return (
    <a href={`mailto:${CONTACT_EMAIL}`} className="text-accent underline-offset-4 hover:underline">
      {CONTACT_EMAIL}
    </a>
  );
}
